import { 
  Flex,
  Accordion,
  AccordionButton, 
  AccordionItem, 
  AccordionPanel,
  HStack, 
  Text,
} from "@chakra-ui/react";
import { RCarCard } from "components";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Cars } from "api/generated/graphql";
import { IconChevron, IconGiftGg, IconMagnifyingGg, IconUnlockGg } from "icons";
import { Livery, CarTier, AppliedLivery, RarityTier } from 'types';
import UpgradeCarItem from "./UpgradeCarItem";

const UpgradeCar = ({ car }: { car: Cars }) => {
  return (
    <Flex
      flexDir="column"
      w="full"
      borderRadius="8px"
      border="1px solid rgba(255, 255, 255, 0.1)"
    >
      <RCarCard 
        carId={car.id}
        tier={car?.tier?.name as CarTier || 'bronze'}
        vehicleName={car.name as string}
        rarity={car.rarity as RarityTier || 'common'}
        timesUsed={car?.timesUsed}
        maximumTimesUsed={car?.maximumTimesUsed}
        hasCardBg={true}
        showTopRightButtons
        livery={car.livery as Livery}
        hasInsetBorder={false}
        borderRadius="0"
        appliedLivery={
          (car.appliedLivery as AppliedLivery) || undefined
        }
        imageProps={{
          transform: 'scale(0.65)',
          marginTop: 8,
        }}
      />
      <Accordion
        allowToggle
        defaultIndex={0}
        variant="trackAccordion"
        border="none"
        borderRadius="0"
      >
        <AccordionItem border="none" borderTop="none">
          {({ isExpanded }) => (
            <>
              <AccordionButton
                width="full"
                background="linear-gradient(180deg, #1E2023 0%, #17181A 100%)"
                _hover={{
                  background: "linear-gradient(180deg, #1E2023 0%, #17181A 100%)",
                }}
                position="relative"
              >
                <HStack>
                  <Flex
                    background="white.10"
                    px="4"
                    py="2"
                    borderRadius="4px"
                    transition="all 0.2s ease"
                    _hover={{
                      background: 'white.20',
                    }}
                    alignItems="center"
                    alignSelf="stretch"
                  >
                    <IconChevron 
                      boxSize={4} 
                      transform={!isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                      transition="all 0.2s ease"
                    />
                  </Flex>

                  <Text
                    fontSize={{ base: '0.875rem', md: '1.125rem'}}
                    lineHeight={{ base: '1.125rem', md: '1.5rem' }}
                    color={isExpanded ? 'white.95' : 'white.60'}
                    fontWeight="400"
                    fontFamily="heading"
                  >
                    INCLUDED
                  </Text>
                </HStack>
              </AccordionButton>
              <AccordionPanel
                background="linear-gradient(180deg, #1E2023 0%, #17181A 100%)"
                p="4"
              >
                <Flex w="100%">
                <Swiper
                  spaceBetween={10}
                  slidesPerView={1}
                  modules={[Pagination]}
                  pagination={{
                    enabled: false,
                    clickable: true,
                    type: 'progressbar',
                    horizontalClass: 'swiper-pagination-progressbar-track',
                  }}
                  breakpoints={{
                    320: {
                      slidesPerView: 3,
                    },
                    768: {
                      slidesPerView: 3,
                    },
                    1024: {
                      slidesPerView: 2.5,
                    },
                    1280: {
                      slidesPerView: 3,
                    },
                  }}
                >
                  <SwiperSlide>
                    <UpgradeCarItem
                      icon={<IconMagnifyingGg />}
                      title="REVEAL"
                      backgroundGradient="linear-gradient(0deg, rgba(0, 255, 232, 0.15) 0%, rgba(0, 255, 232, 0) 50%)"
                      borderGradient="linear-gradient(360deg, rgba(0, 255, 232, 0.4) 0%, rgba(0, 255, 232, 0.16) 100%)"
                    > 
                      <Text
                        fontSize={{ base: '0.625rem', md: '0.75rem' }}
                        lineHeight={{ base: '0.75rem', md: '0.875rem' }}
                        fontWeight="500"
                        color="white.80"
                        whiteSpace="pre-line"
                        textAlign="center"
                      >
                        {`Speed, Fast Turns, \n Top Tracks`}
                      </Text>
                    </UpgradeCarItem>
                  </SwiperSlide>
                  <SwiperSlide>
                    <UpgradeCarItem
                      icon={<IconUnlockGg />}
                      title="ACCESS"
                      backgroundGradient="linear-gradient(0deg, rgba(79, 197, 186, 0.2) 0%, rgba(79, 197, 186, 0) 50%)"
                      borderGradient="linear-gradient(360deg, rgba(0, 255, 232, 0.4) 0%, rgba(0, 255, 232, 0.16) 100%)"
                    >
                      <Text
                        fontSize={{ base: '0.625rem', md: '0.75rem' }}
                        lineHeight={{ base: '0.75rem', md: '0.875rem' }}
                        fontWeight="500"
                        color="white.80"
                        whiteSpace="pre-line"
                        textAlign="center"
                      >
                        {`Leaderboards,\n Market, Fusion`}
                      </Text>
                    </UpgradeCarItem>
                  </SwiperSlide>
                  <SwiperSlide>
                    <UpgradeCarItem
                      icon={<IconGiftGg />}
                      title="REWARDS"
                      backgroundGradient="linear-gradient(0deg, rgba(79, 197, 186, 0.2) 0%, rgba(79, 197, 186, 0) 50%)"
                      borderGradient="linear-gradient(360deg, rgba(0, 255, 232, 0.4) 0%, rgba(0, 255, 232, 0.16) 100%)"
                    >
                      <Text
                        fontSize={{ base: '0.625rem', md: '0.75rem' }}
                        lineHeight={{ base: '0.75rem', md: '0.875rem' }}
                        fontWeight="500"
                        color="white.80"
                        whiteSpace="pre-line"
                        textAlign="center"
                      >
                        {`Race Limit +430\n Mystery Livery`}
                      </Text>
                    </UpgradeCarItem>
                  </SwiperSlide>
                </Swiper>
                </Flex>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </Flex>
  );
};

export default UpgradeCar;